import { useRef, useEffect } from "react"
import Modal from "shared/components/modal"
import classnames from "classnames"
import LoaderDots from "shared/components/loader_dots"

import webModal from "shared/components/web_modal.module.scss"
import styles from "./generator_modal.module.scss"
import HintPill from "../hint_pill"

interface Props {
  askAItoGenerateFilterCode: () => void
  onClose: () => void
  prompt: string
  setPrompt: (value: string) => void
  showAIGenerator: boolean
  generatedCode: string
  isGenerating: boolean
  aiError: string
  rateLimited: boolean
  clearAiError: () => void
}

const AIGeneratorModal = (props: Props) => {
  const { askAItoGenerateFilterCode, onClose, prompt, setPrompt, showAIGenerator, generatedCode, isGenerating, aiError, rateLimited, clearAiError } = props
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const outputTextAreaRef = useRef<HTMLTextAreaElement>(null)

  const onCopyClick = () => {
    if (generatedCode) {
      navigator.clipboard.writeText(generatedCode)
        .then(() => {
          window.appendFlash("Copied to clipboard", "success")
          onClose()
        })
        .catch(error => {
          console.error("Clipboard write failed:", error)
          window.appendFlash("Could not copy automatically. Please copy the code manually from the editor.", "error")
        })
    }
  }

  // Focus on input textarea when modal opens
  useEffect(() => {
    if (showAIGenerator && textAreaRef.current) {
      textAreaRef.current.focus()
    }
  }, [showAIGenerator])

  return (
    <div className={classnames(webModal["web-modal"], styles["ai-generator-modal"])}>
      <Modal show={showAIGenerator} closeListener={onClose} includeCloseX={false}>
        <h2 className="text-h2">Generate filter code with AI</h2>
        <p className="body-medium">
          AI has access to the foundation of your Applet automations (triggers, queries, and actions.) Provide a clear prompt with the desired outcome to generate the filter code.
        </p>

        <div className={styles["form-container"]}>
          {/* Input field */}
          <div className={styles["field-wrapper"]}>
            <div className={styles["connector-line"]} />
            <div className={styles.form}>
              <label className="text-h5" htmlFor="ai-generator-prompt">Prompt</label>
              <textarea
                id="ai-generator-prompt"
                ref={textAreaRef}
                className={styles["textarea"]}
                placeholder="Ex. If the trigger runs during my work hours, then skip the action"
                value={prompt}
                onChange={evt => {
                  setPrompt(evt.currentTarget.value)
                  if (aiError) clearAiError()
                }}
                onKeyDown={evt => {
                  if (evt.key === "Enter" && !evt.shiftKey && !isGenerating && !rateLimited && prompt.length > 0) {
                    evt.preventDefault()
                    askAItoGenerateFilterCode()
                  }
                }}
                readOnly={isGenerating || rateLimited}
              />
            </div>
            <div className={styles["button-row"]}>
              <button
                type="button"
                className={classnames("button-tertiary", { [styles.asking]: isGenerating })}
                onClick={askAItoGenerateFilterCode}
                disabled={isGenerating || rateLimited || prompt.length === 0}
              >
                {isGenerating ? (
                  <>
                    Asking AI
                    <LoaderDots className={styles.loader} />
                  </>
                ) : (
                  "Generate code"
                )}
              </button>
            </div>
            {aiError && (
              <HintPill tone="error" variant="on-light">
                {aiError}
              </HintPill>
            )}
          </div>

          {/* Output field */}
          <div className={styles["field-wrapper"]}>
            <div className={styles.form}>
              <label className="text-h5" htmlFor="ai-generator-output">Output</label>
              <textarea
                id="ai-generator-output"
                ref={outputTextAreaRef}
                className={classnames(styles["textarea"], styles["code-font"])}
                placeholder="Generated code will display here."
                value={generatedCode}
                readOnly
              />
            </div>
            <div className={webModal.ctas}>
              <button
                type="button"
                className="button-tertiary button-cancel-link"
                onClick={onClose}
              >
                Close
              </button>
              <button
                type="button"
                className="button-tertiary"
                onClick={onCopyClick}
                disabled={!generatedCode}
              >
                Copy to clipboard
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default AIGeneratorModal
